import { useCallback, useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { List, Divider, ListItem, Paper } from '@material-ui/core';
import _, { isNumber } from 'lodash';
import NumberFormat from 'react-number-format';
import { BiExpand } from 'react-icons/bi';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import {
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaRegQuestionCircle,
  FaExclamationTriangle,
} from 'react-icons/fa';
import clsx from 'clsx';

import useStyles from './styles';
import AddButton from '../../../../AddButton';
import Button from '../../../../Button';
import {
  addToTrip,
  removeFromTrip,
  saveTripRequest,
  setSelfBookingAllowed,
} from '../../../../../store/slices/trip';
import {
  getRoomDescriptionRequest,
  getRoomDetailRequest,
  setCurrentRateDetailCode,
} from '../../../../../store/slices/hotel';
import HotelAddUpdateToBrochure from '../../../AddUpdateBrochure/HotelAddUpdateToBrochure';
import {
  HotelProviders,
  HotelRateDisplayPreferences,
  PageLinks,
  RoomCategoryFilters,
  SearchModes,
  SearchStatus,
  TravelTypes,
  checkDoSearchDeepLinkWithoutDate,
} from '../../../../../constants';
import AgentCommission from '../../HotelItinerary/AgentCommission';
import HotelCancellationLabel from '../../HotelCancellationLabel';
import Label from '../../../../Label';
import withSpinner from '../../../../../HOCs/withSpinner';
import RateDetail from './RateDetail';
import ReferLabelRoomDescription from '../ReferLabelRoomDescriptionDetail';
import RateDetailDropdownPopover from '../../HotelItinerary/RateDetailDropdownPopover/index.tsx';
import checkDoSkipTripConfiguration from '../../../../../helpers/selectors/checkDoSkipTripConfiguration';
import TripBookButton from '../../../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';
import useResetSaveTripStatus from '../../../../../hooks/useResetSaveTripStatus';
import getHotelRateToDisplay from '../../../../../helpers/getHotelRateToDisplay.ts';
import getHotelPriceDisplayPreference from '../../../../../helpers/selectors/getHotelPriceDisplayPreference';
import RoomRateDescription from '../../RoomRateDescription';
import currencySymbol from '../../../../../helpers/currencySymbol';
import useScrollToElementById from '../../../../../hooks/useScrollToElementById';
import getElementId from '../../../../../helpers/getElementId';
import getPaymentTypeLabel from '../../../../../helpers/getPaymentTypeLabel';
import DetailedRoomDescription from '../DetailedRoomDescription';
import checkDoShowMarkupRates from '../../../../../helpers/selectors/checkDoShowMarkupRates';
import getHotelRate from '../../../../../helpers/selectors/getHotelRate.ts';
import { getHotelRateCodesRequest } from '../../../../../store/slices/user';
import RoomImagesModal from './RoomImage';
import RateCodeKeys from '../../HotelFilterBar/RateCodeKey';
import CustomSearchBar from '../../../../Input/CustomInput/customSearchBar';
import CustomDropDown from '../../../../Input/CustomInput/customDropDown';
import NightlyPriceDisplay from '../NightlyPriceDisplay';
import {
  formatRoomDescription,
  joinRoomTypeAndBeds,
  removeCommissionTerms,
} from '../../../../../helpers/formatters/formatRoomDescription';
import ModalSpinnerSaveTrip from '../../../../ModalSpinner/ModalSpinnerSaveTrip';
import checkDoEnableTripConfigDecoupling from '../../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import formatTripIdForBrochure from '../../../../../helpers/formatters/formatTripIdForBrochure';
import RoomDescription from './RoomDescription';
import getRateActionPlan from '../../../../../helpers/getRateActionPlan';

/// Ensure TripServices.GetFullRawDescription() in backend is updated
/// whenever changes in following method to make sure hotel improved
/// description retrieved from cache when save trip
export const getFullRawDescription = (product) => {
  let description = `${product.rooms[0].desc}\n`;
  if (product.hotelMandatoryFees && product.hotelMandatoryFees.length) {
    description += `\nMandatory Fees: ${_.map(product.hotelMandatoryFees, (m) => `${m.name}: ${m.value}`).join(', ')}`;
  }
  if (product.valueAdds && product.valueAdds.length) {
    description += `\nMeal: ${product.valueAdds.join(',')}`;
  }
  if (product.amenities && product.amenities.length) {
    description += `\nAmenities: ${product.amenities.join(', ')}`;
  }
  if (product.cancellationPolicy) {
    description += `\nCancellation information: ${product.cancellationPolicy}`;
  }
  if (product.additional_info) {
    description += `\n${product.additional_info}`;
  }
  return description;
};

export const getRateNameFromCode = (
  code,
  rates,
  doShowRateCodesFoldedIntoGroupInLabel,
  useSubtext,
  doShowRateCodeGroupName,
) => {
  if (!code || !rates) return undefined;
  const foundRate = rates.find((r) => r.rateCode === code);
  if (doShowRateCodeGroupName && foundRate && foundRate.group) {
    return foundRate.group.groupName;
  }
  if (useSubtext) {
    return foundRate && (foundRate.subText || foundRate.text)
      ? `${foundRate.subText || foundRate.text} ${
          doShowRateCodesFoldedIntoGroupInLabel ? `(${code})` : ''
        }`
      : code;
  }
  return foundRate && foundRate.text
    ? `${foundRate.text} ${doShowRateCodesFoldedIntoGroupInLabel ? `(${code})` : ''}`
    : code;
};

/** Own row above labels | price for grouped expanded rates. */
function getGroupedRateExpandedTitle(room, params) {
  if (!room) return null;
  const {
    doShowAgentCommission,
    hotelRateCodes,
    doShowRateCodesFoldedIntoGroupInLabel,
    doShowRateCodeGroupName,
  } = params;
  const category =
    doShowAgentCommission || !(room.category || '').toLowerCase().includes('commission')
      ? room.category
      : undefined;
  if (category) return category;
  const code = room.ratePlanClientId || room.ratePlanCode;
  if (code && hotelRateCodes && hotelRateCodes.length) {
    const name = getRateNameFromCode(
      code,
      hotelRateCodes,
      doShowRateCodesFoldedIntoGroupInLabel,
      false,
      doShowRateCodeGroupName,
    );
    if (name) return name;
  }
  if (room.type || (room.bedTypes && room.bedTypes.length > 0)) {
    return joinRoomTypeAndBeds(room);
  }
  return null;
}

const UNDEFINED_CATEGORY_LABEL = 'Undefined Category';

/** Light divider: between rates (inset wrapper) and between / under room-type headers when grouped. */
const GROUPED_RATE_DIVIDER_HR_CLASS = 'border-0 border-t border-gray-300 w-full opacity-40 my-0';

const TRIP_BOOK_BUTTON_GROUP_STYLES_COMPACT = {
  minWidth: 'unset',
  paddingTop: '0.25rem',
  paddingBottom: '0.25rem',
};
const TRIP_BOOK_BUTTON_GROUP_STYLES_FULL = {
  ...TRIP_BOOK_BUTTON_GROUP_STYLES_COMPACT,
  width: '100%',
};

function getPrimaryRoomImageUrl(product) {
  const imgs = product?.rooms?.[0]?.images;
  if (!imgs?.length) return null;
  return imgs[0]?.url || null;
}

/** If every rate shares the same primary image URL, return that room's `images`; else null. */
function getGroupConsensusRoomImages(products) {
  if (!products?.length) return null;
  const urls = products.map(getPrimaryRoomImageUrl);
  if (!urls.every(Boolean)) return null;
  const first = urls[0];
  if (!urls.every((u) => u === first)) return null;
  return products[0].rooms[0].images;
}

const RoomsDetails = ({
  hotelDetails,
  tripId,
  onModalClose,
  doShowPrice,
  doShowAgentCommission,
  tripRoomSearchInput,
  isAnyRateFromHotelAddedToTrip,
}) => {
  const classes = useStyles();
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const [isRestrictedRateOpen, setIsRestrictedRateOpen] = useState(false);
  const productsCodesAddingToBrochure = useSelector(
    (state) => state.brochure?.productsCodesAddingToBrochure ?? [],
  );
  const [hotelRateCodes, setHotelRateCodes] = useState();
  const { pageName, segment: segmentIdToUpdate } = router.query;
  const isConfirmationPage = router.route.indexOf('confirmation') > -1;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const { isViewingBrochure, updateBrochurePriceStatus } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const {
    getRoomDetailsStatus = {},
    roomDescriptions,
    currentRateDetailCodes = {},
    productsCallingFromTripConfig = [],
  } = useSelector((state) => state.hotel);
  const currentRateDetailCode = currentRateDetailCodes?.[hotelDetails.hotelKey];
  const {
    travelAgency: currentTravelAgency,
    isLoggedIn,
    email: loggedInUserEmail,
  } = useSelector((state) => state.user);
  const doHaveHotelRateCodes = useSelector((state) => state.user.hotelRateCodes?.length > 0);
  const brochure = useSelector((state) => state.brochure.brochures[tripId]);
  const productsCodesAddedToBrochure = useSelector(
    (store) => store?.brochure?.productsCodesAddedToBrochure ?? [],
  );
  const doAllowBook = brochure && brochure.doAllowBook;

  const doSkipTripConfiguration = useSelector(checkDoSkipTripConfiguration);
  const pricingPreference = useSelector(getHotelPriceDisplayPreference());
  const doShowMarkupRates = useSelector(
    checkDoShowMarkupRates({
      mode: TravelTypes.hotel,
      tripId,
    }),
  );
  const { saveSendTripEmailStatus, saveSendTripEmailError } = useSelector((store) => store.trip);
  const scrollToElement = useScrollToElementById();
  const isUpdatingBrochurePrice =
    isViewingBrochure && updateBrochurePriceStatus === SearchStatus.SEARCHING;
  const { tripConfirmation } = useSelector((store) => store.trip);
  const selectedTrip = useSelector(
    (store) =>
      store.trip.selectedTrips[
        isConfirmationPage && tripConfirmation && tripConfirmation.tripId
          ? tripConfirmation.tripId
          : tripId
      ],
  );
  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const saveTripStatus = useSelector(
    (store) => (store.trip.selectedTrips[tripId] || {}).saveTripStatus,
  );
  const isChangeHotelPage = pageName === PageLinks.ChangeHotel.pageName;
  const doCheckoutOnSelect = isChangeHotelPage || pageName === PageLinks.AddHotel.pageName;
  const doShowBookButton = tripId && doSkipTripConfiguration && !doCheckoutOnSelect;
  const hotelRateCodesFromStore = useSelector((store) => store.user.hotelRateCodes);
  const doHideSpecialHotelRates =
    currentTravelAgency && currentTravelAgency.doHideSpecialHotelRates;
  const { doGroupRoomRates: groupRates, disableMultipleRoomBookings } =
    (currentTravelAgency && currentTravelAgency.settings && currentTravelAgency.settings) || {};
  const doGroupRoomRates = groupRates && !isConfirmationPage;
  const {
    doShowChatGPTImprovedRoomDescription,
    doShowOriginalShorterRoomDescription,
    doAllowShowingRawRateDescription,
    doShowRateCodesFoldedIntoGroupInLabel,
    doShowRateCodeGroupName,
    doAllowDeepLinkWithoutDate,
    selectRoomButtonLabel,
    doSwapGroupedRateTitleAndDescription,
    roomRateCodesDropdownLabel,
    noRateCodesFoundLabel,
  } = useSelector((store) => {
    const { travelAgency } = store.user;
    const settings = (travelAgency || {}).settings || {};
    const filterSettings = (travelAgency || {}).filterSettings || {};

    return {
      doShowChatGPTImprovedRoomDescription: settings.doShowChatGPTImprovedRoomDescription || false,
      doShowOriginalShorterRoomDescription: settings.doShowOriginalShorterRoomDescription || false,
      doAllowShowingRawRateDescription: settings.doAllowShowingRawRateDescription || false,
      doAllowDeepLinkWithoutDate: settings.doAllowDeepLinkWithoutDate || false,
      doShowRateCodesFoldedIntoGroupInLabel:
        settings.doShowRateCodesFoldedIntoGroupInLabel !== undefined &&
        settings.doShowRateCodesFoldedIntoGroupInLabel !== null
          ? settings.doShowRateCodesFoldedIntoGroupInLabel
          : true,
      selectRoomButtonLabel: settings.selectRoomButtonLabel || false,
      doShowRateCodeGroupName: settings.doShowRateCodeGroupName || false,
      doSwapGroupedRateTitleAndDescription: settings.doSwapGroupedRateTitleAndDescription || false,
      roomRateCodesDropdownLabel: filterSettings.roomRateCodesDropdownLabel || false,
      noRateCodesFoundLabel: filterSettings.noRateCodesFoundLabel || false,
    };
  });

  const redirectToTabletSite = (product) => {
    if (!product || !product.externalBookingUrl) return;
    const url = product.externalBookingUrl;

    track('roomsDetails.redirectToTabletSite', {
      product: JSON.stringify(product),
      loggedInUserEmail,
      travelAgencyId: currentTravelAgency?.id,
      externalUrl: url,
    });

    window.open(url, '_blank');
  };

  const isDeepLinkWithoutDateSearch = useMemo(
    () => checkDoSearchDeepLinkWithoutDate(router.query, doAllowDeepLinkWithoutDate),
    [doAllowDeepLinkWithoutDate, router.query],
  );

  const checkIsSelected = useCallback(
    (item) => {
      if (doShowBookButton) return false;
      if (
        selectedTrip &&
        selectedTrip.hotelItineraries &&
        Object.keys(selectedTrip.hotelItineraries).length > 0
      ) {
        return Object.values(selectedTrip.hotelItineraries).some(
          (hotelItinerary) => hotelItinerary.selectedRoomCode === item.code,
        );
      }
      return false;
    },
    [selectedTrip, doShowBookButton],
  );

  const handleRemoveFromTrip = useCallback(
    (roomCode) => {
      if (
        !(
          selectedTrip &&
          selectedTrip.hotelItineraries &&
          Object.keys(selectedTrip.hotelItineraries).length > 0
        )
      ) {
        return;
      }

      const hotelItinerary = Object.values(selectedTrip.hotelItineraries).find(
        (hi) => hi.selectedRoomCode === roomCode,
      );
      if (!hotelItinerary) return;

      dispatch(
        removeFromTrip({
          tripId,
          key: 'hotelItineraries',
          id: hotelItinerary.id,
        }),
      );
    },
    [selectedTrip, dispatch, tripId],
  );

  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const doShowPricing = !!(doShowPrice || doAllowBook);

  const handleScroll = useCallback(
    (event) => {
      if (!event) return;
      if (doEnableTripConfigDecoupling) {
        const containerWrapper = document.getElementById('main-section-wrapper');
        containerWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const elementId = getElementId(event, '.parentDiv');
      if (elementId) {
        scrollToElement(elementId, 'banner');
      } else {
        scrollToElement(tripId, 'banner');
      }
    },
    [doEnableTripConfigDecoupling, scrollToElement, tripId],
  );

  const handleSelectRoomClick = (event, product) => {
    if (isBrochurePage) {
      dispatch(setSelfBookingAllowed());
    }
    if (tripId) {
      const doShowMarkupAmount =
        doShowMarkupRates && product.roomPrice.markupPrice !== product.roomPrice.originalPrice;

      const newItinerary = {
        ...hotelDetails,
        selectedRoomCode: product.code,
        selectedProduct: product,
        hotel: {
          ...hotelDetails.hotel,
          price: {
            ...hotelDetails.hotel.price,
            doShowMarkupAmount,
          },
        },
        cost: {
          ...hotelDetails.cost,
          doShowMarkupAmount,
        },
        id: +_.uniqueId(),
      };
      const updatedTrip = {
        ...(isBrochurePage ? {} : selectedTrip || {}),
        selectedRoomCodes: disableMultipleRoomBookings
          ? [product.code]
          : [
              ...(selectedTrip && selectedTrip.selectedRoomCodes
                ? selectedTrip.selectedRoomCodes
                : []),
              product.code,
            ],
        hotelItineraries: disableMultipleRoomBookings
          ? [newItinerary]
          : // oxlint-disable-next-line no-nested-ternary
            [
              ...(isBrochurePage
                ? []
                : selectedTrip?.hotelItineraries &&
                    Object.keys(selectedTrip.hotelItineraries).length > 0
                  ? Object.values(selectedTrip.hotelItineraries)
                  : []),
              newItinerary,
            ],
      };

      dispatch(
        addToTrip({
          tripId,
          trip: updatedTrip,
        }),
      );

      if (onModalClose) onModalClose();
      handleScroll(event);
      return updatedTrip;
    }

    track('roomsDetails.handleSelectRoomClick', {
      product: JSON.stringify(product),
    });
    return undefined;
  };

  const handleCheckoutOnRoomClick = (product) => {
    if (saveTripStatus === SearchStatus.SEARCHING) return;

    let tripSegmentId = +segmentIdToUpdate || undefined;
    if (selectedTrip.hotelItineraries && selectedTrip.hotelItineraries.length) {
      if (selectedTrip.hotelItineraries.length === 1) {
        tripSegmentId = selectedTrip.hotelItineraries[0].tripSegmentId;
      } else if (tripSegmentId >= 0) {
        let selectedHotelItinerary = Object.values(selectedTrip.hotelItineraries || {}).find(
          (hotelItinerary) => hotelItinerary.tripSegmentId === tripSegmentId,
        );
        if (
          !selectedHotelItinerary &&
          selectedTrip.hotelItineraries &&
          selectedTrip.hotelItineraries.length
        ) {
          [selectedHotelItinerary] = selectedTrip.hotelItineraries;
        }
        if (selectedHotelItinerary) tripSegmentId = selectedHotelItinerary.tripSegmentId;
      }
    }

    const request = {
      tripId,
      ignoreUpdateTripOnSuccess: true,
      trip: {
        tripId: doShowBookButton ? undefined : tripId,
        selectedRoomCodes: [product.code],
        hotelItineraries: [
          {
            ...hotelDetails,
            selectedRoomCode: product.code,
            tripSegmentId: isChangeHotelPage ? tripSegmentId : 0,
            id: +_.uniqueId(),
          },
        ],
        doChangeItineraryInBookedTrip: pageName === PageLinks.ChangeHotel.pageName,
        doAddItineraryInBookedTrip: pageName === PageLinks.AddHotel.pageName,
      },
    };

    dispatch(saveTripRequest(request));
    track('roomsDetails.handleCheckoutOnRoomClick', {
      product: JSON.stringify(product),
    });
    if (onModalClose) onModalClose();
  };

  const handleRateDescriptionToggle = (product, isBrochureToggleEvent) => {
    const code = currentRateDetailCode === product.code ? undefined : product.code;
    if (!isBrochureToggleEvent) {
      dispatch(setCurrentRateDetailCode({ hotelKey: hotelDetails.hotelKey, rateCode: code }));
    }

    const { doCallGetProductInfo, doCallEnhanceDesc, doAddToBrochure } = getRateActionPlan(
      product,
      isBrochureToggleEvent,
      code,
      currentTravelAgency,
      roomDescriptions,
      productsCodesAddedToBrochure,
      false,
    );

    if (doCallGetProductInfo) {
      dispatch(
        getRoomDetailRequest({
          hotelProducts: [product],
          hotelKey: hotelDetails.hotelKey,
          searchKey: hotelDetails.qs,
          hotelProviderId: hotelDetails.hotel.providerId,
          doCallEnhanceDesc,
          doAddToBrochure,
          isAnyRateFromHotelAddedToTrip,
        }),
      );
    } else if ((isConfirmationPage || isBrochurePage) && doCallEnhanceDesc) {
      dispatch(
        getRoomDescriptionRequest({
          code,
          description: getFullRawDescription(product),
          currencyCode: product.roomPrice.propertyCurrencyCode || product.roomPrice.currencyCode,
        }),
      );
    }
  };

  const showProductPricing = useCallback(
    (product, { largePrice = false } = {}) => {
      const hotelRate = getHotelRate({
        rate: product.roomPrice,
        doShowMarkupRate: doShowMarkupRates,
      });
      const rateToDisplay = getHotelRateToDisplay(pricingPreference, hotelRate);

      let packageRate = 0;
      if (!doShowMarkupRates) {
        const rate = getHotelRate({
          rate: product.roomPrice,
          doShowMarkupRate: true,
        });
        packageRate = getHotelRateToDisplay(pricingPreference, rate);
        packageRate = packageRate !== rateToDisplay ? packageRate : 0;
      }
      return (
        <div
          className={clsx(
            'font-bold text-right',
            largePrice ? 'text-lg text-gray-900 leading-snug' : 'leading-tight',
            onModalClose && !largePrice && 'text-sm',
          )}
        >
          {packageRate > 0 && (
            <NumberFormat
              value={packageRate}
              displayType="text"
              thousandSeparator
              prefix={currencySymbol(product.roomPrice.currencyCode)}
              fixedDecimalScale="true"
              decimalScale="2"
              className={clsx(
                'line-through inline-block font-bold',
                largePrice ? 'text-sm' : 'text-base',
              )}
            />
          )}
          <NumberFormat
            value={rateToDisplay}
            displayType="text"
            thousandSeparator
            prefix={currencySymbol(product.roomPrice.currencyCode)}
            fixedDecimalScale="true"
            decimalScale="2"
            className={clsx('ml-1', largePrice ? 'block leading-none' : 'block lg:inline-block')}
          />
          {pricingPreference === HotelRateDisplayPreferences.TotalRate && (
            <div
              className={clsx(
                'font-normal text-gray-600',
                largePrice ? 'text-xs mt-px leading-tight' : 'text-sm mt-px',
              )}
            >
              Total stay
            </div>
          )}
          {pricingPreference !== HotelRateDisplayPreferences.TotalRate && (
            <NightlyPriceDisplay pricingPreference={pricingPreference} />
          )}
          {packageRate > 0 && (
            <div className="text-xs text-gray-600 font-normal mb-1">Package discount</div>
          )}
        </div>
      );
    },
    [doShowMarkupRates, onModalClose, pricingPreference],
  );

  useResetSaveTripStatus(
    doShowBookButton ? saveSendTripEmailStatus : undefined,
    saveSendTripEmailError,
  );

  useEffect(() => {
    if (hotelRateCodesFromStore) {
      setHotelRateCodes(hotelRateCodesFromStore);
    } else {
      dispatch(getHotelRateCodesRequest());
    }
  }, [dispatch, hotelRateCodesFromStore]);

  const handleRestrictedRateClicked = () => {
    setIsRestrictedRateOpen((v) => !v);
  };

  const filterProductsByCategory = useMemo(() => {
    if (!doHideSpecialHotelRates) {
      return {
        productsWithRestrictedRates: [],
        productsWithoutRestrictedRates: hotelDetails.hotel.products,
      };
    }

    const productsWithRestrictedRates = _.filter(hotelDetails.hotel.products || [], (product) =>
      _.some(product.rooms || [], (room) =>
        _.some(RoomCategoryFilters, (category) => _.includes(room.category, category)),
      ),
    );
    const productsWithoutRestrictedRates = _.filter(hotelDetails.hotel.products || [], (product) =>
      _.every(
        product.rooms || [],
        (room) => !_.some(RoomCategoryFilters, (category) => _.includes(room.category, category)),
      ),
    );

    return {
      productsWithRestrictedRates,
      productsWithoutRestrictedRates,
    };
  }, [doHideSpecialHotelRates, hotelDetails.hotel.products]);

  const { productsWithRestrictedRates, productsWithoutRestrictedRates } = filterProductsByCategory;
  const [filteredProducts, setFilteredProducts] = useState(
    !doHideSpecialHotelRates ? hotelDetails.hotel.products : productsWithoutRestrictedRates,
  );
  const productPrices = useMemo(
    () =>
      productsWithRestrictedRates.map((product) => {
        const hotelRate = getHotelRate({
          rate: product.roomPrice,
          doShowMarkupRate: true,
        });
        const rateToDisplay = getHotelRateToDisplay(0, hotelRate);
        return rateToDisplay;
      }),
    [productsWithRestrictedRates],
  );

  const minimumPrice = Math.min(...productPrices);
  const [doshowImagesModal, setDoshowImagesModal] = useState(false);
  const [selectedProductImages, setSelectedProductImages] = useState([]);

  const [bedType, setBedType] = useState('');
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [category, setCategory] = useState('');
  const [ratePlanCode, setRatePlanCode] = useState('');
  const [groupCodes, setGroupCodes] = useState('');
  const [query, setQuery] = useState('');

  const handleCloseImagesModel = () => {
    setDoshowImagesModal(false);
  };

  const handleOpenImagesModel = (images) => {
    setSelectedProductImages(images);
    setDoshowImagesModal(true);
  };

  const hasAnyRoomWithImages = useMemo(
    () =>
      hotelDetails.hotel.products &&
      !!_.find(
        hotelDetails.hotel.products,
        (p) => p.rooms[0]?.images && p.rooms[0]?.images?.length,
      ),
    [hotelDetails.hotel.products],
  );

  // Extract bed types and room categories
  const allBedTypes = useMemo(() => {
    const bedTypes = new Set();

    hotelDetails.hotel.products.forEach((product) =>
      product.rooms.forEach((room) => {
        if (Array.isArray(room.bedTypes)) {
          // Check if bedTypes is a valid array
          room.bedTypes.forEach((bt) => bedTypes.add(bt));
        }
      }),
    );

    return [...bedTypes]; // Convert Set to Array
  }, [hotelDetails]);

  const allRoomCategories = useMemo(() => {
    const roomCategories = new Set();
    hotelDetails.hotel.products.forEach((product) =>
      product.rooms.forEach((room) => {
        if (room.category) roomCategories.add(room.category);
      }),
    );
    return [...roomCategories];
  }, [hotelDetails]);

  const allRoomTypes = useMemo(() => {
    const roomTypes = new Set();
    hotelDetails.hotel.products.forEach((product) =>
      product.rooms.forEach((room) => {
        roomTypes.add(room.type || UNDEFINED_CATEGORY_LABEL);
      }),
    );
    return [...roomTypes];
  }, [hotelDetails]);

  const doHideSabreFilters = useMemo(
    () => hotelDetails?.hotel?.products?.every((product) => product?.externalBookingUrl),
    [hotelDetails],
  );

  const allRatePlanCodes = useMemo(() => {
    // Extract rate codes
    const ratePlanCodes = new Set();
    hotelDetails.hotel.products.forEach((product) => {
      product.rooms.forEach((room) => {
        const code = room.ratePlanClientId || room.ratePlanCode;
        if (code) ratePlanCodes.add(code);
      });
    });

    // Map rate plan codes to their corresponding names
    const ratePlanNames = new Set();
    ratePlanCodes.forEach((code) => {
      const match = hotelRateCodes?.find(
        (hotelRate) => hotelRate.rateCode.toLowerCase() === code.toLowerCase(),
      );
      if (!match) return;
      if (doShowRateCodeGroupName && match.group) {
        ratePlanNames.add(match.group.groupName);
      } else {
        ratePlanNames.add(match.text);
      }
    });
    return Array.from(ratePlanNames);
  }, [hotelDetails, hotelRateCodes, doShowRateCodeGroupName]);

  const filterProducts = useCallback(() => {
    // Convert query to lowercase for case-insensitive matching
    const lowerCaseQuery = query.toLowerCase().trim();
    const hotelProducts = !doHideSpecialHotelRates
      ? hotelDetails.hotel.products
      : productsWithoutRestrictedRates;
    const products = hotelProducts.filter(
      (product) =>
        (!category || product.rooms.some((room) => room.category === category)) &&
        (!ratePlanCode ||
          product.rooms.some((room) =>
            doShowRateCodeGroupName
              ? groupCodes.includes(room.ratePlanCode) && groupCodes.includes(room.ratePlanClientId)
              : room.ratePlanCode === ratePlanCode || room.ratePlanClientId === ratePlanCode,
          )) &&
        (!bedType || product.rooms.some((room) => room.bedTypes.includes(bedType))) &&
        (!selectedRoomType ||
          product.rooms.some((room) =>
            selectedRoomType === UNDEFINED_CATEGORY_LABEL
              ? !room.type
              : room.type === selectedRoomType,
          )) &&
        product.rooms.some(
          (room) =>
            (room.desc && room.desc.toLowerCase().includes(lowerCaseQuery)) ||
            (room.category && room.category.toLowerCase().includes(lowerCaseQuery)) ||
            (room.type && room.type.toLowerCase().includes(lowerCaseQuery)) ||
            (room.bedTypes &&
              room.bedTypes.some((roomBedType) =>
                roomBedType.toLowerCase().includes(lowerCaseQuery),
              )),
        ),
    );

    setFilteredProducts(products);
  }, [
    bedType,
    category,
    doHideSpecialHotelRates,
    doShowRateCodeGroupName,
    hotelDetails.hotel.products,
    productsWithoutRestrictedRates,
    query,
    ratePlanCode,
    groupCodes,
    selectedRoomType,
  ]);

  const filterProductsByKeyWord = useCallback(({ keyWord }) => {
    setQuery(keyWord);
  }, []);

  const onSuiteChange = useCallback((roomCategory) => {
    setCategory(roomCategory);
  }, []);

  const onBedChange = useCallback((bed) => {
    setBedType(bed);
  }, []);

  const onSelectedRoomTypeChange = useCallback((bed) => {
    setSelectedRoomType(bed);
  }, []);

  const onRateCodeChange = useCallback(
    (selectedValue) => {
      if (!selectedValue) {
        setRatePlanCode('');
        setGroupCodes([]);
        return;
      }

      if (doShowRateCodeGroupName) {
        const group = hotelRateCodes.find(
          (r) => r.group?.groupName?.toLowerCase() === selectedValue.toLowerCase(),
        );
        if (group && group.group) {
          const codes = hotelRateCodes
            .filter((r) => r.group?.groupName === group.group.groupName)
            .map((r) => r.rateCode);
          setGroupCodes(codes);
          setRatePlanCode(group.group.groupName);
          return;
        }

        setGroupCodes([]);
        setRatePlanCode('');
      } else {
        const matchedRateCode = hotelRateCodes.find(
          (code) => code.text.toLowerCase() === selectedValue.toLowerCase(),
        );
        setRatePlanCode(matchedRateCode ? matchedRateCode.rateCode : '');
        setGroupCodes([]);
      }
    },
    [hotelRateCodes, doShowRateCodeGroupName],
  );

  useEffect(() => {
    filterProducts();
  }, [query, bedType, category, ratePlanCode, groupCodes, filterProducts]);

  const groupRoomsByDescription = (products) => {
    const groupedRooms = {};
    products.forEach((product) => {
      product.rooms.forEach((room) => {
        let description =
          room.originalFormattedDesc ||
          room.formattedDesc ||
          formatRoomDescription(room.originalDesc || room.desc);

        if (!doShowAgentCommission) {
          description = removeCommissionTerms(description);
        }

        if (!groupedRooms[description]) {
          groupedRooms[description] = [];
        }
        groupedRooms[description].push(product);
      });
    });

    return groupedRooms;
  };
  // Group hotel rooms by their long description
  const groupedRoomRates =
    doGroupRoomRates && filteredProducts && groupRoomsByDescription(filteredProducts);

  // Calculate minimum rate of grouped rooms to show 'from {minimum price of rate}'
  const calculateMinimumRoomPrice = (products) => {
    const minPrice = products.reduce((price, product) => {
      if (product.roomPrice) {
        const hotelRate = getHotelRate({
          rate: product.roomPrice,
          doShowMarkupRate: doShowMarkupRates,
        });
        const rateToDisplay = getHotelRateToDisplay(pricingPreference, hotelRate);

        return price === null ? rateToDisplay : Math.min(price, rateToDisplay);
      }
      return price;
    }, null);
    if (minPrice === null) {
      return null;
    }
    if (minPrice % 1 !== 0) {
      return minPrice.toFixed(2); // Ensure two decimal places for non-integer values
    }
    return minPrice;
  };

  const getRemoveLabel = useCallback(
    (product) => {
      if (disableMultipleRoomBookings) return 'Remove';

      if (
        !selectedTrip ||
        !selectedTrip.hotelItineraries ||
        !Array.isArray(selectedTrip.hotelItineraries) ||
        !selectedTrip.hotelItineraries.length > 0
      )
        return 'Remove';
      const selectedProducts = selectedTrip.hotelItineraries.filter(
        (x) => x.selectedRoomCode === product.code,
      );
      return `${selectedProducts.length}${selectedProducts.length === 1 ? ' Room' : ' Rooms'} Selected`;
    },
    [disableMultipleRoomBookings, selectedTrip],
  );

  const addRemoveRateToTripButtonSize = useMemo(
    () =>
      isNumber(selectedTrip || onModalClose ? Button.buttonSizes.XS : undefined)
        ? Button.buttonSizes.XS
        : Button.buttonSizes.SM,
    [onModalClose, selectedTrip],
  );

  const getAddRemoveRateButtonVariant = useCallback(
    (product) =>
      checkIsSelected(product) ? Button.buttonVariants.INLINE : Button.buttonVariants.PRIMARY,
    [checkIsSelected],
  );

  const getRedirectButtonLabel = useMemo(() => {
    switch (hotelDetails?.hotel?.priceProvider) {
      case HotelProviders.TABLET:
        return 'Book on Tablet Pro';
      default:
        return 'Reserve';
    }
  }, [hotelDetails]);

  const RoomListItems = (products) => {
    const groupedTitleParams = {
      doShowAgentCommission,
      hotelRateCodes,
      doShowRateCodesFoldedIntoGroupInLabel,
      doShowRateCodeGroupName,
    };

    return (
      products &&
      products.map((product, index, array) => {
        const hasPaymentStatusLabel =
          product.isPrepaidRate ||
          (product.paymentTypeCode !== undefined && product.paymentTypeCode !== null);

        /** In grouped mode photos live at the room-type header; hide per-rate image slot
         *  regardless of whether the header has a consensus photo (prevents "No photo"
         *  placeholders stacking under rates when the group lacks a header image). */
        const hideRateRowImage = doGroupRoomRates;

        const groupedTitleLine = doGroupRoomRates
          ? getGroupedRateExpandedTitle(product.rooms && product.rooms[0], groupedTitleParams)
          : null;

        /** Price rail only when `layoutGrouped`; full-width row under labels|price when true. */
        const renderExpandedRateActions = (layoutGrouped) => {
          const full = layoutGrouped;
          return (
            <>
              {product.externalBookingUrl ? (
                <Button
                  size={Button.buttonSizes.SM}
                  onClick={() => redirectToTabletSite(product)}
                  className={clsx('rounded-full px-5', full && 'w-full')}
                >
                  {getRedirectButtonLabel}
                </Button>
              ) : null}
              {tripId &&
                !isViewingBrochure &&
                !isBrochurePage &&
                !doCheckoutOnSelect &&
                !doShowBookButton &&
                !product.externalBookingUrl &&
                (disableMultipleRoomBookings ? (
                  <AddButton
                    isSelected={checkIsSelected(product)}
                    onAdd={(event) => {
                      handleSelectRoomClick(event, product);
                    }}
                    onRemove={() => handleRemoveFromTrip(product.code)}
                    addLabel={selectRoomButtonLabel || 'Add to Trip'}
                    removeLabel={getRemoveLabel(product)}
                    buttonSize={selectedTrip || onModalClose ? Button.buttonSizes.XS : undefined}
                    className={clsx('rounded-full px-5 shadow-sm', full && 'w-full')}
                  />
                ) : (
                  <div
                    className={clsx(
                      'flex flex-nowrap shadow-sm rounded-full overflow-hidden',
                      full && 'w-full',
                    )}
                  >
                    {full ? (
                      <>
                        {/* flex 1 : 4 : 1 → each end is 1/4 the width of the center segment */}
                        <div className={classes.roomCardAddTripSegmentSide}>
                          <Button
                            size={addRemoveRateToTripButtonSize}
                            onClick={() => handleRemoveFromTrip(product.code)}
                            doDiscardRadius
                            className="w-full rounded-l-full border-r-0 focus:outline-none px-2 py-2"
                            variant={getAddRemoveRateButtonVariant(product)}
                          >
                            −
                          </Button>
                        </div>
                        <div className={classes.roomCardAddTripSegmentCenter}>
                          <AddButton
                            isSelected={checkIsSelected(product)}
                            onAdd={(event) => {
                              handleSelectRoomClick(event, product);
                            }}
                            onRemove={() => handleRemoveFromTrip(product.code)}
                            addLabel={selectRoomButtonLabel || 'Add to Trip'}
                            removeLabel={getRemoveLabel(product)}
                            buttonSize={
                              selectedTrip || onModalClose ? Button.buttonSizes.XS : undefined
                            }
                            doDiscardRadius
                            className="w-full pl-3 pr-3 py-2 focus:outline-none"
                          />
                        </div>
                        <div className={classes.roomCardAddTripSegmentSide}>
                          <Button
                            size={addRemoveRateToTripButtonSize}
                            onClick={(event) => {
                              handleSelectRoomClick(event, product);
                            }}
                            doDiscardRadius
                            className="w-full rounded-r-full border-l-0 focus:outline-none px-2 py-2"
                            variant={getAddRemoveRateButtonVariant(product)}
                          >
                            +
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Button
                          size={addRemoveRateToTripButtonSize}
                          onClick={() => handleRemoveFromTrip(product.code)}
                          doDiscardRadius
                          className="rounded-l-full border-r-0 focus:outline-none pl-3 pr-2 py-2"
                          variant={getAddRemoveRateButtonVariant(product)}
                        >
                          −
                        </Button>
                        <AddButton
                          isSelected={checkIsSelected(product)}
                          onAdd={(event) => {
                            handleSelectRoomClick(event, product);
                          }}
                          onRemove={() => handleRemoveFromTrip(product.code)}
                          addLabel={selectRoomButtonLabel || 'Add to Trip'}
                          removeLabel={getRemoveLabel(product)}
                          buttonSize={
                            selectedTrip || onModalClose ? Button.buttonSizes.XS : undefined
                          }
                          doDiscardRadius
                          className="pl-4 pr-4 py-2 focus:outline-none"
                        />
                        <Button
                          size={addRemoveRateToTripButtonSize}
                          onClick={(event) => {
                            handleSelectRoomClick(event, product);
                          }}
                          doDiscardRadius
                          className="rounded-r-full border-l-0 focus:outline-none pl-3 pr-3 py-2"
                          variant={getAddRemoveRateButtonVariant(product)}
                        >
                          +
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              {tripId && doCheckoutOnSelect && !product.externalBookingUrl && (
                <Button
                  onClick={() => handleCheckoutOnRoomClick(product)}
                  size={Button.buttonSizes.SM}
                  className={clsx('rounded-full', full && 'w-full')}
                >
                  Select
                </Button>
              )}
              {((doShowBookButton && !isViewingBrochure) ||
                (isBrochurePage && (doAllowBook || isLoggedIn))) &&
                !product.roomPrice.isUnavailableRate &&
                !isUpdatingBrochurePrice &&
                !product.externalBookingUrl && (
                  <div className={full ? 'w-full' : undefined}>
                    <TripBookButton
                      tripId={tripId}
                      saveTripStatus={saveTripStatus}
                      saveSendTripEmailStatus={saveSendTripEmailStatus}
                      saveSendTripEmailError={saveSendTripEmailError}
                      buttonColorCssClass="bg-button-theme text-button-theme"
                      buttonGroupStyles={
                        full
                          ? TRIP_BOOK_BUTTON_GROUP_STYLES_FULL
                          : TRIP_BOOK_BUTTON_GROUP_STYLES_COMPACT
                      }
                      onBookClick={() => handleSelectRoomClick(undefined, product)}
                      tripRoomSearchInput={tripRoomSearchInput}
                    />
                  </div>
                )}
            </>
          );
        };

        return (
          <Fragment key={product.code}>
            <List className="block" disablePadding={doGroupRoomRates}>
              <ListItem
                classes={{ root: classes.root }}
                className={clsx(
                  'break-words !p-0',
                  checkIsSelected(product) ? 'bg-theme bg-opacity-10' : '',
                )}
              >
                <div
                  className={clsx(
                    'w-full flex flex-col gap-4',
                    doGroupRoomRates
                      ? 'pl-3 md:pl-4 pr-4 md:pr-5 pt-2 pb-2 md:pt-3 md:pb-3'
                      : 'p-4 md:p-5',
                  )}
                >
                  <div
                    className={clsx(
                      'flex flex-col md:flex-row md:items-stretch min-w-0',
                      doGroupRoomRates ? 'gap-3 md:gap-4' : 'gap-4',
                    )}
                  >
                    {hasAnyRoomWithImages && !hideRateRowImage && (
                      <div className={classes.roomCardImageWrap}>
                        {product.rooms &&
                        product.rooms[0] &&
                        product.rooms[0].images &&
                        product.rooms[0].images.length > 0 ? (
                          <>
                            <img
                              src={product.rooms[0].images[0].url}
                              alt={product.rooms[0].images[0].caption}
                              className="email-img block w-full h-36 md:h-full min-h-36 object-cover rounded-lg flex-shrink-0"
                            />
                            <button
                              type="button"
                              className="absolute top-2 left-2 bg-black bg-opacity-70 text-white cursor-pointer rounded-lg p-1"
                              title="Click to view hotel room images in modal"
                              onClick={() => handleOpenImagesModel(product.rooms[0].images)}
                            >
                              <BiExpand className="text-white text-md m-0.5" />
                            </button>
                          </>
                        ) : (
                          <Paper className="email-hidden-field flex items-center justify-center w-full h-36 md:h-full min-h-36 text-center text-sm bg-gray-200 rounded-lg text-gray-600 flex-shrink-0">
                            No photo
                          </Paper>
                        )}
                      </div>
                    )}

                    <div className="flex-1 min-w-0 flex flex-col min-h-0">
                      {/* Grouped: rate name row, then labels (left) | price rail (right). Ungrouped: flex row md. */}
                      {groupedTitleLine && (
                        <div className="font-bold text-lg text-gray-900 leading-snug w-full mb-2">
                          {doShowAgentCommission
                            ? groupedTitleLine
                            : removeCommissionTerms(groupedTitleLine)}
                        </div>
                      )}
                      <div
                        className={clsx(
                          'w-full min-w-0',
                          /* Tailwind v1 has no arbitrary grid-cols; plain `grid` stacks as one column. */
                          doGroupRoomRates
                            ? 'flex flex-row flex-nowrap items-start gap-x-4 md:gap-x-8'
                            : 'flex flex-col md:flex-row items-start gap-4',
                        )}
                      >
                        <div className="min-w-0 flex flex-1 flex-col">
                          {!doGroupRoomRates && (
                            <RoomRateDescription
                              room={product.rooms && product.rooms[0]}
                              doShowRawGDSDescription={!doShowChatGPTImprovedRoomDescription}
                              doShowOriginalShorterRoomDescription={
                                doShowOriginalShorterRoomDescription
                              }
                              doShowAgentCommission={doShowAgentCommission}
                              doEmphasizeRoomTitle
                            />
                          )}
                          <div className={classes.roomCardGuaranteeLine}>
                            {[
                              hasPaymentStatusLabel && (
                                <span key="payment-type" className={classes.roomCardGuaranteeMuted}>
                                  {product.isPrepaidRate
                                    ? 'Prepaid Rate'
                                    : getPaymentTypeLabel(
                                        product.paymentTypeCode,
                                        currentTravelAgency,
                                      )}
                                </span>
                              ),
                              ...(product.valueAdds || []).map((v, vi) => (
                                <span
                                  key={`value-add-${vi}`}
                                  className={classes.roomCardGuaranteeMuted}
                                >
                                  {v}
                                </span>
                              )),
                              doShowPrice && (
                                <HotelCancellationLabel
                                  key="cancellation"
                                  product={product}
                                  variant="plain"
                                />
                              ),
                            ]
                              .flat()
                              .filter(Boolean)
                              .map((node, i) => (
                                <Fragment key={`guarantee-${i}`}>
                                  {i > 0 && (
                                    <span className={classes.roomCardGuaranteeBullet} aria-hidden>
                                      {' '}
                                      ·{' '}
                                    </span>
                                  )}
                                  {node}
                                </Fragment>
                              ))}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {product.isMatchWithRequestedRatePlanCode ? (
                              <div className="inline-flex flex-wrap items-center gap-2">
                                <span className={classes.roomCardProgramPill}>
                                  {getRateNameFromCode(
                                    product.rooms[0].ratePlanClientId ||
                                      product.rooms[0].ratePlanCode,
                                    hotelRateCodes,
                                    doShowRateCodesFoldedIntoGroupInLabel,
                                    false,
                                    doShowRateCodeGroupName,
                                  )}
                                </span>
                                <RateCodeKeys
                                  className="flex items-center"
                                  icon={
                                    <FaRegQuestionCircle
                                      className="text-blue-600 ml-0.5"
                                      size={18}
                                    />
                                  }
                                  variant="white"
                                  filterSpacingClass="py-1 px-2"
                                  label=""
                                  selectedSearchMode={SearchModes.HOTEL}
                                />
                              </div>
                            ) : null}
                            {!product.isMatchWithRequestedRatePlanCode && doHaveHotelRateCodes && (
                              <Label
                                text="Not a Preferred Rate"
                                icon={<FaTimes />}
                                level="danger"
                                inline
                                descriptionOnHover="Preferred partner client perks may not apply"
                              />
                            )}
                            {product.loyaltyIdRequiredAtReservation && (
                              <Label
                                text="Loyalty ID Required at Reservation"
                                icon={<FaExclamationTriangle />}
                                level="warning"
                                inline
                                descriptionOnHover="A customer loyalty ID must be provided at the time of reservation for this rate"
                              />
                            )}
                            {product.qualificationIdRequiredAtCheckIn && (
                              <Label
                                text="Qualification ID Required at Check-In"
                                icon={<FaExclamationTriangle />}
                                level="warning"
                                inline
                                descriptionOnHover="A rate qualification ID must be presented at check-in to obtain this rate"
                              />
                            )}
                          </div>
                          <div className="mt-2">
                            <ReferLabelRoomDescription
                              room={product && product.rooms && product.rooms[0]}
                            />
                          </div>
                        </div>

                        <div
                          className={
                            doGroupRoomRates
                              ? classes.roomCardRightRailInRateGrid
                              : classes.roomCardRightRail
                          }
                        >
                          {doShowPrice && product.roomPrice && (
                            <>
                              <div className="flex flex-col w-full items-end text-right">
                                {withSpinner(
                                  () =>
                                    !product.roomPrice.isUnavailableRate ? (
                                      showProductPricing(product, { largePrice: true })
                                    ) : (
                                      <span className="italic text-base">Room unavailable</span>
                                    ),
                                  isUpdatingBrochurePrice,
                                  'Retrieving updated price',
                                  'inline',
                                  'xs',
                                )}
                              </div>
                              <div className={classes.roomCardPriceRailSpacerTight} />
                              <RateDetailDropdownPopover
                                roomPrice={product.roomPrice}
                                numberOfNights={hotelDetails.duration}
                                popoverAlignClass="right-0"
                                fontSize="text-sm"
                                alignClass="justify-end"
                                dropdownLabel="Price breakdown"
                                tripId={tripId}
                                doShowMarkupRates={doShowMarkupRates}
                                showChevron
                                triggerClassName="font-normal"
                              />
                              <div className={classes.roomCardPriceRailSpacerLoose} />
                            </>
                          )}
                          {doGroupRoomRates ? (
                            <div className="hidden md:block w-full">
                              <div className="flex flex-wrap gap-2 w-full justify-end">
                                {renderExpandedRateActions(false)}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2 w-full justify-end">
                              {renderExpandedRateActions(false)}
                            </div>
                          )}
                        </div>
                      </div>
                      {doGroupRoomRates && (
                        <div className="w-full mt-3 min-w-0 md:hidden">
                          <div className="flex flex-col gap-2 w-full">
                            {renderExpandedRateActions(true)}
                          </div>
                        </div>
                      )}

                      <div
                        className={clsx(
                          'gap-2 pt-1 pb-1 mt-1',
                          doGroupRoomRates
                            ? 'flex flex-col items-center text-center md:flex-row md:flex-wrap md:items-center md:justify-between md:text-left'
                            : 'flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center sm:justify-between',
                        )}
                      >
                        <div
                          className={clsx(
                            'flex flex-wrap items-center gap-3 min-w-0',
                            doGroupRoomRates && 'w-full justify-center md:w-auto md:justify-start',
                          )}
                        >
                          {!productsCodesAddingToBrochure.includes(product.code) &&
                            !productsCallingFromTripConfig.includes(product.code) &&
                            withSpinner(
                              () => (
                                <button
                                  type="button"
                                  className="text-sm text-theme cursor-pointer z-10 inline-flex items-center font-normal bg-transparent border-0 p-0"
                                  onClick={() => handleRateDescriptionToggle(product)}
                                >
                                  {currentRateDetailCode === product.code ? (
                                    <>
                                      Hide Full Description
                                      <FaChevronUp className="pl-1" />
                                    </>
                                  ) : (
                                    <>
                                      <span>View Full Description</span>
                                      <FaChevronDown className="pl-1" />
                                    </>
                                  )}
                                </button>
                              ),
                              getRoomDetailsStatus[product.code] === SearchStatus.SEARCHING,
                              'Getting room rate details...',
                              'left-inline',
                              'xs',
                            )}
                          {!isViewingCompare &&
                            !productsCallingFromTripConfig.includes(product.code) && (
                              <HotelAddUpdateToBrochure
                                tripId={formatTripIdForBrochure(
                                  tripId,
                                  hotelDetails.startDateTime,
                                  hotelDetails.endDateTime,
                                  staterooms,
                                )}
                                hotelItinerary={hotelDetails}
                                product={product}
                                className={
                                  doGroupRoomRates
                                    ? 'text-center text-sm md:text-left'
                                    : 'text-left text-sm'
                                }
                                getImprovedRateDescription={() =>
                                  handleRateDescriptionToggle(product, true)
                                }
                                tripRoomSearchInput={tripRoomSearchInput}
                                productsCodesAddingToBrochure={productsCodesAddingToBrochure}
                              />
                            )}
                        </div>
                        {doShowAgentCommission && (
                          <div
                            className={
                              doGroupRoomRates
                                ? classes.roomCardCommissionTextGroupedFooter
                                : classes.roomCardCommissionText
                            }
                          >
                            <AgentCommission
                              agentCommission={product.roomPrice.agentCommission}
                              isCommissionable={product.roomPrice.isCommissionable}
                              currencyCode={product.roomPrice.currencyCode}
                              agentCommissionPercentage={
                                product.roomPrice.agentCommissionPercentage
                              }
                              doShowCommissionAsPercentage={
                                !!(
                                  currentTravelAgency &&
                                  currentTravelAgency.settings &&
                                  currentTravelAgency.settings.doShowHotelComissionInPercentage
                                )
                              }
                              className="p-0 m-0 bg-transparent"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {currentRateDetailCode === product.code && (
                    <div className="w-full border-t border-gray-100">
                      {doShowChatGPTImprovedRoomDescription &&
                        !product?.rooms?.[0]?.htmlDescription && (
                          <DetailedRoomDescription
                            description={roomDescriptions[currentRateDetailCode]}
                            rawDescription={getFullRawDescription(product)}
                            doAllowShowingRawRateDescription={doAllowShowingRawRateDescription}
                            doShowAgentCommission={doShowAgentCommission}
                          />
                        )}
                      {!doShowChatGPTImprovedRoomDescription &&
                        !product?.rooms?.[0]?.htmlDescription && <RateDetail product={product} />}
                      {product?.rooms?.[0]?.htmlDescription && (
                        <RoomDescription description={product?.rooms?.[0]?.htmlDescription} />
                      )}
                    </div>
                  )}
                </div>
              </ListItem>
            </List>
            {index < array.length - 1 ? (
              <div
                className={clsx(doGroupRoomRates ? 'pl-3 md:pl-4 pr-4 md:pr-5' : 'px-4 md:px-5')}
              >
                <hr className={GROUPED_RATE_DIVIDER_HR_CLASS} aria-hidden />
              </div>
            ) : null}
          </Fragment>
        );
      })
    );
  };

  // Visibility flags for each room rate group
  const [visibleGroups, setVisibleGroups] = useState(
    doGroupRoomRates
      ? Object.keys(groupedRoomRates).reduce(
          (acc, description) => ({ ...acc, [description]: false }),
          {},
        )
      : null,
  );

  // Toggle visibility of room rate group
  const toggleVisibility = (description) => {
    setVisibleGroups((prev) => ({ ...prev, [description]: !prev[description] }));
  };
  return (
    <div>
      {!isDeepLinkWithoutDateSearch && (
        <div className="flex flex-wrap items-center gap-2 w-full mb-3">
          <CustomSearchBar onChange={(value) => filterProductsByKeyWord({ keyWord: value })} />
          {!doHideSabreFilters && (
            <CustomDropDown
              options={allRatePlanCodes}
              label={roomRateCodesDropdownLabel || 'Rate Codes'}
              onChange={onRateCodeChange}
              noOptionsMessage={noRateCodesFoundLabel || 'No preferred partner rates found'}
            />
          )}
          <CustomDropDown options={allRoomCategories} label="Categories" onChange={onSuiteChange} />
          <CustomDropDown options={allBedTypes} label="Bed Types" onChange={onBedChange} />
          <CustomDropDown
            options={allRoomTypes}
            label="Room Types"
            onChange={onSelectedRoomTypeChange}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        {!doGroupRoomRates && (
          <span className="font-bold">
            <span className="font-subHeading text-subHeading">
              {isDeepLinkWithoutDateSearch
                ? 'Select checkin/checkout dates to view room rates'
                : 'Rooms and Rates'}
            </span>
          </span>
        )}
        {!doHideSabreFilters && (
          <RateCodeKeys
            icon={
              <FaRegQuestionCircle
                className="text-green-600 mr-1 place-self-center min-w-4"
                size={20}
              />
            }
            variant="GrayFilterClasses"
            filterSpacingClass="py-2 px-3"
            selectedSearchMode={SearchModes.HOTEL}
          />
        )}
      </div>
      {doGroupRoomRates &&
        Object.entries(groupedRoomRates).map(([key, value], index) => {
          const roomLabel = joinRoomTypeAndBeds(value[0].rooms[0]);

          const title = doSwapGroupedRateTitleAndDescription ? key : roomLabel;
          const roomSizeSubtitle = value[0]?.rooms?.[0]?.roomSize;
          const subtitle = doSwapGroupedRateTitleAndDescription
            ? roomLabel
            : roomSizeSubtitle || key;
          const consensusRoomImages = getGroupConsensusRoomImages(value);
          const showConsensusPhoto =
            hasAnyRoomWithImages && consensusRoomImages && consensusRoomImages.length > 0;
          const groupRatesExpanded = Boolean(visibleGroups && visibleGroups[key]);
          const viewRatesButtonLabel = groupRatesExpanded
            ? 'Hide rates'
            : `View ${value.length} ${value.length === 1 ? 'rate' : 'rates'}`;

          return (
            <div key={key}>
              {index > 0 ? <hr className={GROUPED_RATE_DIVIDER_HR_CLASS} aria-hidden /> : null}
              <div className="pt-4 md:pt-5">
                <div
                  className={clsx(
                    classes.roomGroupHeaderGrid,
                    showConsensusPhoto
                      ? classes.roomGroupHeaderGrid_hasPhoto
                      : classes.roomGroupHeaderGrid_noPhoto,
                  )}
                >
                  {showConsensusPhoto && (
                    <div
                      className={clsx(
                        classes.roomGroupHeaderImageWrap,
                        classes.roomGroupHeaderPhotoSpan,
                      )}
                    >
                      <div className={classes.roomGroupHeaderImageInner}>
                        <img
                          src={consensusRoomImages[0].url}
                          alt={consensusRoomImages[0].caption}
                          className={clsx('email-img', classes.roomGroupHeaderImage)}
                        />
                        <button
                          type="button"
                          className={clsx(
                            classes.roomGroupHeaderExpandBtn,
                            'bg-black bg-opacity-70 text-white cursor-pointer rounded-lg p-1',
                          )}
                          title="Click to view hotel room images in modal"
                          onClick={() => handleOpenImagesModel(consensusRoomImages)}
                        >
                          <BiExpand className="text-white text-md m-0.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p
                      className={clsx(
                        'text-gray-900 leading-snug',
                        doSwapGroupedRateTitleAndDescription
                          ? 'font-semibold text-sm md:text-base'
                          : 'font-bold text-base md:text-lg',
                      )}
                    >
                      {title}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">{subtitle}</p>
                  </div>
                  <div className={classes.roomGroupHeaderPriceRail}>
                    {doShowPricing && (
                      <>
                        <div className="text-right">
                          <span className="text-sm font-normal text-gray-600">from </span>
                          <NumberFormat
                            value={calculateMinimumRoomPrice(value)}
                            displayType="text"
                            thousandSeparator
                            prefix={currencySymbol(value[0].roomPrice.currencyCode)}
                            fixedDecimalScale="true"
                            decimalScale="2"
                            className="inline-block text-base font-bold text-gray-900"
                          />
                        </div>
                        {pricingPreference !== HotelRateDisplayPreferences.TotalRate && (
                          <div className="text-right">
                            <NightlyPriceDisplay pricingPreference={pricingPreference} />
                          </div>
                        )}
                      </>
                    )}
                    <Button
                      size={Button.buttonSizes.SM}
                      onClick={() => toggleVisibility(key)}
                      className="w-full shadow-sm md:w-auto"
                    >
                      <span className="inline-flex items-center">
                        {viewRatesButtonLabel}
                        {groupRatesExpanded ? (
                          <FaChevronUp className="inline-block ml-1" aria-hidden />
                        ) : (
                          <FaChevronDown className="inline-block ml-1" aria-hidden />
                        )}
                      </span>
                    </Button>
                  </div>
                </div>
                {groupRatesExpanded && (
                  <>
                    <hr className={GROUPED_RATE_DIVIDER_HR_CLASS} aria-hidden />
                    <div className="pb-2 md:pb-2">{RoomListItems(value)}</div>
                  </>
                )}
              </div>
            </div>
          );
        })}

      {!doGroupRoomRates &&
        (tripConfirmation && tripConfirmation.tripId && !doShowPrice
          ? RoomListItems(_.filter(filteredProducts, (p) => checkIsSelected(p)))
          : RoomListItems(filteredProducts))}
      {doShowBookButton && !isViewingBrochure && <ModalSpinnerSaveTrip tripId={tripId} />}

      {!isDeepLinkWithoutDateSearch && (!filteredProducts || !filteredProducts.length) ? (
        <div className="mt-4">No rates available.</div>
      ) : (
        <></>
      )}

      {productsWithRestrictedRates.length > 0 && !isConfirmationPage && (
        <>
          <br />
          <Divider />
          <div className="flex justify-around">
            <div
              className="flex gap-1 text-orange-600 cursor-pointer items-center gap-2 font-bold mt-2"
              onKeyDown={() => {}}
              onClick={handleRestrictedRateClicked}
              role="button"
              tabIndex={0}
            >
              <p>
                Some rates have been hidden due to restriction criteria
                {doShowPricing && (
                  <>
                    {' '}
                    (
                    <NumberFormat
                      value={minimumPrice}
                      displayType="text"
                      thousandSeparator
                      prefix={currencySymbol(productsWithRestrictedRates[0].roomPrice.currencyCode)}
                      fixedDecimalScale="true"
                      decimalScale="2"
                      suffix="+"
                    />
                    )
                  </>
                )}
              </p>
              {!isRestrictedRateOpen ? (
                <FaChevronDown className="inline-block" />
              ) : (
                <FaChevronUp className="inline-block" />
              )}
            </div>
          </div>
          {isRestrictedRateOpen && RoomListItems(productsWithRestrictedRates)}
        </>
      )}

      <RoomImagesModal
        doShow={doshowImagesModal}
        onClose={handleCloseImagesModel}
        images={selectedProductImages}
      />
    </div>
  );
};

RoomsDetails.propTypes = {
  hotelDetails: PropTypes.shape().isRequired,
  tripId: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowPrice: PropTypes.bool,
  doShowAgentCommission: PropTypes.bool,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
  isAnyRateFromHotelAddedToTrip: PropTypes.bool,
};

RoomsDetails.defaultProps = {
  tripId: '',
  onModalClose: undefined,
  doShowPrice: true,
  doShowAgentCommission: true,
  tripRoomSearchInput: undefined,
  isAnyRateFromHotelAddedToTrip: false,
};

export default RoomsDetails;
